import { useState, useEffect } from 'react';
import {
	SimpleGrid,
	Flex,
	Stack,
	Heading,
	Icon,
	Image,
	Tooltip,
	Spinner,
	StackDivider,
	Spacer,
	Tag
} from '@chakra-ui/react';
import {
	BiDollar,
	BiCalendar
} from 'react-icons/bi';
import {
	VscEdit
} from 'react-icons/vsc';
import { MdVisibility, MdCheckCircle, MdError, MdPending, MdGroupWork, MdWarning, MdFilter, MdCropOriginal, MdNoteAdd, MdClose, MdImage, MdLinkOff } from 'react-icons/md';
import { IoIosArrowRoundUp, IoIosArrowRoundDown } from "react-icons/io";
import { ListingModel, ArtistModel } from 'models';
import { ListingStatus } from 'models/Listing/types';
import Feature from '../../Feature/Feature';
import BasicArtistCard from 'components/Artist/BasicArtistCard/BasicArtistCard';
import { AiFillHeart, AiOutlineHeart } from 'react-icons/ai';
import { RiAuctionLine } from 'react-icons/ri';


export interface ListingContainerProps {
	listing: ListingModel;
	artist?: ArtistModel | null;
	isFollowingArtist?: boolean;
	followArtistLoading?: boolean;
	onEditClick: () => void;
	onViewSourceClick: () => void;
	onRankChangeClick?: () => void;
	onGroupingClick?: () => void;
	onCreateItemClick?: () => void;
	onMarkAsFakeClick?: () => void;
	onMarkAsSetClick?: () => void;
	onMarkAsOriginalClick?: () => void;
	onRejectClick?: () => void;
	onUnlinkClick?: () => void;
	unlinkLoading?: boolean;
	isDemoted?: boolean;
	onFollowArtistClick?: () => void;
	onArtistClick?: () => void;
	onMigrateImageClick?: () => void;
	migrateImageLoading?: boolean;
}

export enum ListingContainerSizingConfig {
	IMAGE_MAX_HEIGHT = '500px',
	SECTION_SPACING = '20px',
}

export default function ListingContainer({
	listing,
	artist,
	isFollowingArtist = false,
	followArtistLoading = false,
	onEditClick,
	onViewSourceClick,
	onRankChangeClick,
	onGroupingClick,
	onCreateItemClick,
	onMarkAsFakeClick,
	onMarkAsSetClick,
	onMarkAsOriginalClick,
	onRejectClick,
	onUnlinkClick,
	unlinkLoading = false,
	isDemoted = false,
	onFollowArtistClick,
	onArtistClick,
	onMigrateImageClick,
	migrateImageLoading = false
}: ListingContainerProps) {
	const [artistData, setArtistData] = useState<ArtistModel | null | undefined>(artist);

	useEffect(() => {
		if (artist) {
			setArtistData(artist);
		}
	}, [artist]);

	function renderContainerUserButton(icon: React.ElementType, onClick: () => void, isLoading: boolean, disabled: boolean = false) {
		return (
			<Flex
				w={8}
				h={8}
				align={'center'}
				justify={'center'}
				rounded={'full'}
				cursor={disabled ? 'not-allowed' : 'pointer'}
				border="1px"
				onClick={disabled ? undefined : onClick}
				_hover={disabled ? {} : { color: 'blue.500' }}
				opacity={disabled ? 0.4 : 1}
			>
				{isLoading ? <Spinner size="sm" /> : <Icon as={icon} />}
			</Flex>
		);
	}

	function renderContainerUserButtons() {
		const isPending = listing.processingStatus === ListingStatus.PENDING;
		const isApproved = listing.processingStatus === ListingStatus.APPROVED;
		const hasItem = listing.hasAssociatedItem();
		const isSet = listing.isSet();
		const isOriginal = listing.isOriginal();

		return (
			<Stack spacing={2}>
				{/* Row 1: Core actions */}
				<Flex direction="row" gap={2} justifyContent="center">
					<Tooltip label="Edit Listing">
						{renderContainerUserButton(VscEdit, onEditClick, false, false)}
					</Tooltip>
					<Tooltip label="View Source">
						{renderContainerUserButton(MdVisibility, onViewSourceClick, false, false)}
					</Tooltip>
					{onGroupingClick && (
						<Tooltip label={isPending ? "Create Grouping" : "Grouping: Ineligible"}>
							{renderContainerUserButton(MdGroupWork, onGroupingClick, false, !isPending)}
						</Tooltip>
					)}
					{onCreateItemClick && (
						<Tooltip label={isPending ? "Create Item" : "Create Item: Ineligible"}>
							{renderContainerUserButton(MdNoteAdd, onCreateItemClick, false, !isPending)}
						</Tooltip>
					)}
					{onUnlinkClick && (
						<Tooltip label={hasItem ? "Unlink from Item" : "Unlink: No associated item"}>
							{renderContainerUserButton(MdLinkOff, onUnlinkClick, unlinkLoading, !hasItem)}
						</Tooltip>
					)}
					{onMigrateImageClick && (
						<Tooltip label="Migrate Image">
							{renderContainerUserButton(MdImage, onMigrateImageClick, migrateImageLoading, false)}
						</Tooltip>
					)}
				</Flex>
				
				{/* Row 2: Marking and ranking actions */}
				<Flex direction="row" gap={2} justifyContent="center">
					{onMarkAsSetClick && (
						<Tooltip label={isSet ? "Mark as Set: Already marked as Set" : hasItem ? "Mark as Set: Ineligible" : "Mark as Set"}>
							{renderContainerUserButton(MdFilter, onMarkAsSetClick, false, hasItem || isSet)}
						</Tooltip>
					)}
					{onMarkAsOriginalClick && (
						<Tooltip label={isOriginal ? "Mark as Original: Already marked as Original" : hasItem ? "Mark as Original: Ineligible" : "Mark as Original"}>
							{renderContainerUserButton(MdCropOriginal, onMarkAsOriginalClick, false, hasItem || isOriginal)}
						</Tooltip>
					)}
					{onMarkAsFakeClick && (
						<Tooltip label={isApproved ? "Mark as Fake: Ineligible" : "Mark as Fake"}>
							{renderContainerUserButton(MdWarning, onMarkAsFakeClick, false, isApproved)}
						</Tooltip>
					)}
					{onRejectClick && (
						<Tooltip label={isApproved ? "Reject Listing: Ineligible" : "Reject Listing"}>
							{renderContainerUserButton(MdClose, onRejectClick, false, isApproved)}
						</Tooltip>
					)}
					{onRankChangeClick && (
						<Tooltip label={isDemoted ? "Promote this listing" : "Demote this listing"}>
							{renderContainerUserButton(isDemoted ? IoIosArrowRoundUp : IoIosArrowRoundDown, onRankChangeClick, false, false)}
						</Tooltip>
					)}
				</Flex>
			</Stack>
		);
	}

	function renderArtistSection() {
		if (artistData && onArtistClick) {
			const buttonText = isFollowingArtist ? 'Unfollow' : 'Follow';
			const buttonIcon = isFollowingArtist ? <AiFillHeart /> : <AiOutlineHeart />;

			return (
				<BasicArtistCard
					artist={artistData}
					avatarSize="md"
					titleSize="lg"
					showAdditionalInfo
					showButton
					onArtistTextClick={onArtistClick}
					buttonText={buttonText}
					buttonIcon={buttonIcon}
					buttonLoading={followArtistLoading}
					onButtonClick={onFollowArtistClick || (() => {})}
					showBorder={false}
				/>
			);
		}
	}

	function getProcessingStatusDisplay() {
                switch (listing.processingStatus) {
                        case ListingStatus.APPROVED:
                                return { icon: MdCheckCircle, color: 'green.500', bg: 'green.100' };
                        case ListingStatus.REJECTED:
                                return { icon: MdError, color: 'red.500', bg: 'red.100' };
                        case ListingStatus.GROUPING_PENDING:
                                return { icon: MdGroupWork, color: 'yellow.500', bg: 'yellow.100' };
                        case ListingStatus.SET_PENDING:
                                return { icon: MdPending, color: 'teal.500', bg: 'teal.100' };
                        case ListingStatus.FAKE_PENDING:
                                return { icon: MdPending, color: 'red.500', bg: 'red.100' };
                        case ListingStatus.ORIGINAL_PENDING:
                                return { icon: MdPending, color: 'pink.500', bg: 'pink.100' };
                        case ListingStatus.PENDING:
                        default:
                                return { icon: MdPending, color: 'gray.500', bg: 'gray.100' };
                }
        }

	function renderListingBadges() {
		const badges = [];

		if (listing.isOriginal()) {
			badges.push(
				<Tag size="lg" key="listingTag_original" variant="solid" colorScheme="pink" width="fit-content">
					Original
				</Tag>
			);
		}

		if (listing.isSet()) {
			badges.push(
				<Tag size="lg" key="listingTag_set" variant="solid" colorScheme="teal" width="fit-content">
					Set
				</Tag>
			);
		}

		if (!listing.isAuthentic()) {
			badges.push(
				<Tag size="lg" key="listingTag_fake" variant="solid" colorScheme="red" width="fit-content">
					Fake
				</Tag>
			);
		}

		if (badges.length === 0) {
			return null;
		}

		return (
			<Flex direction="row" gap={2} wrap="wrap">
				{badges}
			</Flex>
		);
	}

	function renderContainerFeatures() {
		const statusDisplay = getProcessingStatusDisplay();

		return (
			<Stack paddingTop={ListingContainerSizingConfig.SECTION_SPACING} spacing={12}>
				{/* Processing Status Section */}
				<Feature
					key="processing-status"
					icon={<Icon as={statusDisplay.icon} color={statusDisplay.color} w={5} h={5} />}
					iconBg={statusDisplay.bg}
					text={listing.processingStatus}
					subText="Processing Status"
				/>

				{/* Market Features Section */}
				<Stack spacing={4} divider={<StackDivider borderColor={'gray.100'} />}>
					{/* Sale Price */}
					{listing.rawPrice && (
						<Feature
							key="sale-price"
							icon={<Icon as={RiAuctionLine} color={'gray.600'} w={5} h={5} />}
							iconBg={'gray.200'}
							text={listing.getRawPriceForDisplay()}
							subText="Sale Price"
						/>
					)}

					{/* Estimate */}
					{listing.hasEstimate() && (
						<Feature
							key="estimate"
							icon={<Icon as={BiDollar} color={'gray.600'} w={5} h={5} />}
							iconBg={'gray.200'}
							text={listing.getEstimateForDisplay()}
							subText="Estimate"
						/>
					)}

					{/* Sale Date */}
					{listing.date && (
						<Feature
							key="sale-date"
							icon={<Icon as={BiCalendar} color={'gray.600'} w={5} h={5} />}
							iconBg={'gray.200'}
							text={listing.getFormattedDate()}
							subText="Sale Date"
						/>
					)}
				</Stack>
			</Stack>
		);
	}

	function renderListingImage() {
		return (
			<Flex justifyContent="center" alignItems="center">
				<Image
					rounded="md"
					alt={listing.name}
					src={listing.image}
					objectFit="contain"
					maxHeight={ListingContainerSizingConfig.IMAGE_MAX_HEIGHT}
				/>
			</Flex>
		);
	}

	return (
		<SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
			<Stack spacing={4}>
				{renderListingImage()}
				<Spacer />
				<Flex justifyContent="center">
					{renderContainerUserButtons()}
				</Flex>
			</Stack>
			<Stack spacing={4}>
				<Heading>{listing.name}</Heading>
				{renderListingBadges()}
				{renderArtistSection()}
				{renderContainerFeatures()}
			</Stack>
		</SimpleGrid>
	);
}
